import { useState, useEffect, useRef, memo } from "react";
import axios from "axios";
import { MdOutlineFileUpload, MdFileDownload } from "react-icons/md";
import { TiTick } from "react-icons/ti";
import { toast } from "react-hot-toast";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import backarrow from "../assets/icon/arrow-right.svg";
import { motion } from "framer-motion";
import DatePicker from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import DateObject from "react-date-object";
import {
  FaBook,
  FaBookOpen,
  FaRegListAlt,
  FaDatabase,
  FaUserGraduate,
  FaChalkboardTeacher,
  FaCalculator,
  FaChartLine,
  FaCoins,
  FaPenFancy,
  FaRegFileAlt,
  FaPencilAlt,
} from "react-icons/fa";

const icons = [
  FaBook,
  FaBookOpen,
  FaRegListAlt,
  FaDatabase,
  FaUserGraduate,
  FaChalkboardTeacher,
  FaCalculator,
  FaChartLine,
  FaCoins,
  FaPenFancy,
  FaRegFileAlt,
  FaPencilAlt,
];

const staticBackgroundIcons = (() => {
  const rows = 5,
    cols = 11,
    arr = [];
  for (let i = 0; i < rows; i++)
    for (let j = 0; j < cols; j++) {
      const Icon = icons[Math.floor(Math.random() * icons.length)];
      arr.push({
        Icon,
        size: 18 + Math.random() * 20,
        top: (i / rows) * 100 + Math.random() * 5,
        left: (j / cols) * 100 + Math.random() * 5,
        duration: 12 + Math.random() * 10,
        opacity: 0.15 + Math.random() * 0.25,
        xOffset: Math.random() * 10 - 5,
        yOffset: Math.random() * 10 - 5,
      });
    }
  return arr;
})();

const AnimatedBackground = memo(() => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {staticBackgroundIcons.map((item, i) => (
      <motion.div
        key={i}
        className="absolute text-white/50"
        style={{
          top: `${item.top}%`,
          left: `${item.left}%`,
          opacity: item.opacity,
        }}
        initial={{ scale: 0.5 }}
        animate={{
          x: [0, item.xOffset, 0],
          y: [0, item.yOffset, 0],
          scale: [0.5, 1, 0.5],
        }}
        transition={{
          duration: item.duration,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      >
        <item.Icon size={item.size} />
      </motion.div>
    ))}
  </div>
));

const EditCourse = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const courseid = location.state?.courseid;
  const API_BASE = process.env.REACT_APP_API_URL;

  const [loading, setLoading] = useState(true);
  const [courseName, setCourseName] = useState("");
  const [description, setDescription] = useState("");
  const [dateValue, setDateValue] = useState(null);
  const [sendDate, setSendDate] = useState("");

  const [teacherName, setTeacherName] = useState("");
  const [teacherCode, setTeacherCode] = useState("");
  const [teachers, setTeachers] = useState([]);
  const [showTeacherList, setShowTeacherList] = useState(false);
  const [teacherLoading, setTeacherLoading] = useState(false);

  const [studentName, setStudentName] = useState("");
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [students, setStudents] = useState([]);
  const [showStudentList, setShowStudentList] = useState(false);
  const [studentLoading, setStudentLoading] = useState(false);

  const [price, setPrice] = useState(0);
  const [hasDiscount, setHasDiscount] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [finalPrice, setFinalPrice] = useState(0);

  const [status, setStatus] = useState("");

  const [uploadImage, setUploadImage] = useState("");
  const [showUploadIcon, setShowUploadIcon] = useState(true);
  const [apiImage, setApiImage] = useState("");
  const [imageId, setImageId] = useState("");

  const teacherRef = useRef(null);
  const studentRef = useRef(null);

  useEffect(() => {
    const p = Number(price) || 0;
    const d = hasDiscount ? Number(discount) || 0 : 0;
    const discounted = p - (p * d) / 100;
    setFinalPrice(Math.round(discounted));
  }, [price, discount, hasDiscount]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (teacherRef.current && !teacherRef.current.contains(e.target))
        setShowTeacherList(false);
      if (studentRef.current && !studentRef.current.contains(e.target))
        setShowStudentList(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const searchTeachers = async (query) => {
    if (!query.trim()) {
      setTeachers([]);
      return;
    }
    setTeacherLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE}/api/teacher/Get-All-Teacher?search=${query}`,
        { headers: { Authorization: localStorage.getItem("token") } }
      );
      setTeachers(res.data.data.teachers || []);
    } catch {
      toast.error("خطا در دریافت اساتید");
      setTeachers([]);
    } finally {
      setTeacherLoading(false);
    }
  };

  const searchStudents = async (query) => {
    if (!query.trim() && query !== "") {
      setStudents([]);
      return;
    }
    setStudentLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE}/api/student/Get-All-Student?search=${query}`,
        { headers: { Authorization: localStorage.getItem("token") } }
      );
      setStudents(res.data.data.students || []);
    } catch {
      toast.error("خطا در دریافت دانشجویان");
      setStudents([]);
    } finally {
      setStudentLoading(false);
    }
  };

  const handleDateChange = (date) => {
    if (!date) {
      setDateValue(null);
      setSendDate("");
      return;
    }
    setDateValue(date);
    const gregorianDate = date.toDate();
    setSendDate(gregorianDate.toISOString().slice(0, 10));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setShowUploadIcon(false);
    const formData = new FormData();
    formData.append("file", file);
    axios
      .post(`${API_BASE}/api/files/admin/upload-file`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: localStorage.getItem("token"),
        },
      })
      .then((res) => {
        toast.success("تصویر آپلود شد");
        setImageId(res.data.data.fileId);
        setUploadImage(URL.createObjectURL(file));
        setApiImage("");
      })
      .catch(() => {
        toast.error("خطا در آپلود");
        setShowUploadIcon(true);
      });
  };

  const fetchCourse = async () => {
    if (!courseid) return navigate("/Mainpage/CoursesInf");
    try {
      const res = await axios.get(
        `${API_BASE}/api/course/Get-Single-Course-By-Id/${courseid}`,
        { headers: { Authorization: localStorage.getItem("token") } }
      );
      const data = res.data.data;

      setCourseName(data.Courseinf.CourseName || "");
      setDescription(data.Courseinf.CourseDescription || "");
      setPrice(data.Courseinf.InitialPrice || 0);
      setDiscount(data.Courseinf.DisCount || 0);
      setHasDiscount(!!data.Courseinf.DisCount);
      setFinalPrice(data.Courseinf.FinalPrice || 0);
      setStatus(data.Courseinf.Status_Course || "");

      if (data.Teacher) {
        setTeacherName(`${data.Teacher.firstName} ${data.Teacher.lastName}`);
        setTeacherCode(data.Teacher.National_Code);
      }

      // <<< مهم: دانشجوهای انتخاب‌شده >>>
      const selectedCodes = data.Courseinf.student_National_Codes || [];
      setSelectedStudents(selectedCodes);

      // تاریخ
      if (data.Courseinf.Date_Classes) {
        const miladiDate = new DateObject(data.Courseinf.Date_Classes);
        const shamsiDate = miladiDate.convert(persian, persian_fa);
        setDateValue(shamsiDate);
        setSendDate(data.Courseinf.Date_Classes);
      }

      // تصویر
      if (data.CoursesImage) {
        setApiImage(`${API_BASE}/${data.CoursesImage.fileName}`);
        setShowUploadIcon(false);
      }

      // <<< اصلاح اصلی مشکل دانشجویان >>>
      // اگر دانشجویی انتخاب شده، لیست همه دانشجوها رو لود کن تا checkboxها درست نمایش داده بشن
      if (selectedCodes.length > 0) {
        await searchStudents(""); // جستجوی خالی → معمولاً همه یا اکثر دانشجوها رو برمی‌گردونه
      }

      setLoading(false);
    } catch (err) {
      toast.error("خطا در دریافت اطلاعات دوره");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourse();
  }, [courseid]);

  const handleEditCourse = async () => {
    if (!sendDate) {
      toast.error("لطفاً تاریخ شروع کلاس را انتخاب کنید");
      return;
    }
    try {
      await axios.post(
        `${API_BASE}/api/course/Edit-Course/${courseid}`,
        {
          CourseName: courseName,
          CourseDescription: description,
          Teacher_National_Code: teacherCode,
          student_National_Codes: selectedStudents,
          Date_Classes: sendDate,
          Status_Course: status,
          imageid: imageId || undefined,
          InitialPrice: Number(price),
          FinalPrice: finalPrice,
          DisCount: hasDiscount ? Number(discount) : 0,
        },
        { headers: { Authorization: localStorage.getItem("token") } }
      );

      toast.success("دوره با موفقیت ویرایش شد");
      navigate("/Mainpage/CoursesInf");
    } catch (err) {
      toast.error(err?.response?.data?.message || "خطا در ویرایش دوره");
    }
  };

  const handleDownload = async () => {
    try {
      const response = await axios.get(
        `${API_BASE}/api/course/create_excel_file_One_Course/${courseid}`,
        {
          responseType: "blob",
          headers: { Authorization: localStorage.getItem("token") },
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Course_Info.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      toast.error("خطا در دانلود فایل");
    }
  };

  if (!localStorage.getItem("token")) return <Navigate to="/" />;

  return (
    <div className="relative min-h-screen w-full bg-gray-800 flex items-center justify-center py-12 px-4">
      <AnimatedBackground />

      <motion.div
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-2xl p-5 w-full max-w-2xl mx-auto border border-white/30"
      >
        <div className="flex items-center justify-between mb-5">
          <img
            src={backarrow}
            alt="back"
            className="w-8 h-8 cursor-pointer hover:scale-110 transition"
            onClick={() => navigate(-1)}
          />
          <h2 className="text-xl font-bold pr-[70px] text-gray-800">
            ویرایش دوره
          </h2>
          {!loading && (
            <div
              onClick={handleDownload}
              className="flex items-center gap-2 cursor-pointer hover:text-gray-600 transition"
            >
              <MdFileDownload size={24} />
              <span className="text-base font-medium">دانلود اکسل</span>
            </div>
          )}
        </div>

        <div className="flex justify-center my-5">
          <div
            onClick={() => document.getElementById("edit-image").click()}
            className="relative w-24 h-24 rounded-full border-[3px] border-gray-700 overflow-hidden cursor-pointer shadow-lg hover:scale-105 transition"
          >
            <input
              id="edit-image"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleImageUpload}
            />
            {showUploadIcon ? (
              <div className="w-full h-full flex items-center justify-center bg-gray-100">
                <MdOutlineFileUpload size={48} className="text-gray-600" />
              </div>
            ) : (
              <img
                src={uploadImage || apiImage}
                alt="course"
                className="w-full h-full object-cover"
              />
            )}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-10 text-gray-600 font-medium">
            در حال دریافت اطلاعات...
          </div>
        ) : (
          <div className="space-y-4 text-sm">
            <Input
              value={courseName}
              setValue={setCourseName}
              placeholder="نام دوره"
            />

            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="توضیحات دوره..."
              rows={3}
              className="w-full p-3 rounded-lg border-2 border-gray-400 focus:border-gray-700 outline-none text-gray-800 font-medium resize-none bg-white/70"
            />

            <div ref={teacherRef} className="relative">
              <input
                type="text"
                value={teacherName}
                onChange={(e) => {
                  setTeacherName(e.target.value);
                  searchTeachers(e.target.value);
                  setShowTeacherList(true);
                }}
                onFocus={() => teacherName && setShowTeacherList(true)}
                placeholder="جستجوی استاد..."
                className="w-full px-4 py-3 rounded-lg border-2 border-gray-400 focus:border-gray-700 outline-none text-gray-800 font-medium bg-white/70 pr-10"
              />
              {showTeacherList && (
                <div className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-xl border border-gray-200 max-h-48 overflow-y-auto">
                  {teacherLoading ? (
                    <div className="p-4 text-center text-gray-500">
                      در حال جستجو...
                    </div>
                  ) : teachers.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      استادی یافت نشد
                    </div>
                  ) : (
                    teachers.map((t) => (
                      <div
                        key={t.National_Code}
                        onClick={() => {
                          setTeacherName(`${t.firstName} ${t.lastName}`);
                          setTeacherCode(t.National_Code);
                          setShowTeacherList(false);
                        }}
                        className="px-4 py-2.5 hover:bg-gray-100 cursor-pointer"
                      >
                        {t.firstName} {t.lastName}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            <div ref={studentRef} className="relative">
              <input
                type="text"
                value={studentName}
                onChange={(e) => {
                  setStudentName(e.target.value);
                  searchStudents(e.target.value);
                  setShowStudentList(true);
                }}
                onFocus={() => studentName && setShowStudentList(true)}
                placeholder="جستجو و انتخاب دانشجوها..."
                className="w-full px-4 py-3 rounded-lg border-2 border-gray-400 focus:border-gray-700 outline-none text-gray-800 font-medium bg-white/70 pr-10"
              />
              {showStudentList && (
                <div className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-xl border border-gray-200 max-h-60 overflow-y-auto">
                  {studentLoading ? (
                    <div className="p-4 text-center text-gray-500">
                      در حال جستجو...
                    </div>
                  ) : students.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      دانشجویی یافت نشد
                    </div>
                  ) : (
                    students.map((s) => (
                      <label
                        key={s.National_Code}
                        className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-100 cursor-pointer"
                      >
                        <span>
                          {s.firstName} {s.lastName}
                        </span>
                        <input
                          type="checkbox"
                          checked={selectedStudents.includes(s.National_Code)}
                          onChange={() =>
                            setSelectedStudents((prev) =>
                              prev.includes(s.National_Code)
                                ? prev.filter((c) => c !== s.National_Code)
                                : [...prev, s.National_Code]
                            )
                          }
                          className="w-5 h-5 rounded border-gray-400"
                        />
                      </label>
                    ))
                  )}
                </div>
              )}
            </div>

            <div className="space-y-3">
              <p className="font-bold text-gray-800">وضعیت دوره:</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: "شروع نشده", value: "Not_Started" },
                  { label: "در حال برگزاری", value: "In_Progress" },
                  { label: "تمام شده", value: "Ended" },
                  { label: "لغو شده", value: "Cancled" },
                ].map((item) => (
                  <label
                    key={item.value}
                    className="flex items-center gap-3 cursor-pointer"
                  >
                    <div
                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition ${
                        status === item.value
                          ? "border-gray-800 bg-gray-800"
                          : "border-gray-400"
                      }`}
                      onClick={() => setStatus(item.value)}
                    >
                      {status === item.value && (
                        <TiTick className="text-white" size={16} />
                      )}
                    </div>
                    <span className="text-gray-700">{item.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <DatePicker
                value={dateValue}
                onChange={handleDateChange}
                calendar={persian}
                locale={persian_fa}
                format="YYYY/MM/DD"
                placeholder="تاریخ شروع کلاس"
                inputClass="w-full px-3 py-3 rounded-lg border-2 border-gray-400 focus:border-gray-700 outline-none text-gray-800 text-sm text-right bg-white/70"
              />
              <Input
                value={price}
                setValue={setPrice}
                placeholder="قیمت (تومان)"
                onlyNumbers
              />
            </div>

            <div className="flex items-center gap-4">
              <span className="font-bold text-gray-800 text-sm">تخفیف؟</span>
              <button
                type="button"
                onClick={() => {
                  setHasDiscount(!hasDiscount);
                  if (!hasDiscount) setDiscount("");
                }}
                className={`relative w-12 h-7 rounded-full transition ${
                  hasDiscount ? "bg-gray-800" : "bg-gray-300"
                }`}
              >
                <span
                  className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition ${
                    hasDiscount ? "translate-x-5" : "translate-x-1"
                  }`}
                />
              </button>
              {hasDiscount && (
                <div className="flex items-center gap-2">
                  <Input
                    value={discount}
                    setValue={setDiscount}
                    placeholder="درصد"
                    maxLength={3}
                    onlyNumbers
                    className="w-20"
                  />
                  <span>%</span>
                </div>
              )}
            </div>

            <div className="bg-gray-100 rounded-lg p-4 text-center">
              <p className="font-bold text-gray-800">
                قیمت نهایی: {finalPrice.toLocaleString()} تومان
              </p>
            </div>

            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleEditCourse}
              className="w-full py-4 bg-gray-800 hover:bg-gray-900 text-white font-bold rounded-lg shadow-xl transition mt-4"
            >
              ذخیره تغییرات
            </motion.button>
          </div>
        )}
      </motion.div>
    </div>
  );
};

const Input = ({
  value,
  setValue,
  placeholder,
  maxLength,
  onlyNumbers,
  className = "",
}) => (
  <input
    type="text"
    value={value}
    onChange={(e) => setValue(e.target.value)}
    placeholder={placeholder}
    maxLength={maxLength}
    onKeyDown={(e) =>
      onlyNumbers &&
      !/[0-9]|Backspace|Delete|ArrowLeft|ArrowRight|Tab/.test(e.key) &&
      e.preventDefault()
    }
    className={`w-full px-4 py-3 rounded-lg border-2 border-gray-400 focus:border-gray-700 outline-none text-gray-800 font-medium text-sm bg-white/70 transition ${className}`}
  />
);

export default EditCourse;
